import React from "react";
import { Monitor, Moon, Sun } from "lucide-react";
import { useTheme } from "../hooks/useTheme";

// Three-way appearance control: System, Light, Dark.
//
// The nav bar's toggle only ever writes an explicit light or dark, so tapping
// it once pinned the theme and the app stopped following the phone from then
// on, with no way back. This is that way back, and it is why the stored
// preference has three states rather than being a boolean.
//
// System is first and is the default, so the common case is the one already
// selected and nobody has to choose. The selected option is marked by fill
// and weight as well as colour, so it does not rely on colour alone.
const OPTIONS = [
  { value: "system", label: "System", Icon: Monitor },
  { value: "light", label: "Light", Icon: Sun },
  { value: "dark", label: "Dark", Icon: Moon },
];

function AppearancePicker() {
  const { preference, setPreference } = useTheme();

  return (
    <div style={{ padding: "8px 14px 10px" }}>
      <div
        id="appearance-label"
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: "var(--color-text-faint)",
          marginBottom: 6,
        }}
      >
        Appearance
      </div>

      <div
        role="radiogroup"
        aria-labelledby="appearance-label"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 2,
          backgroundColor: "var(--color-bg-page)",
          border: "1px solid var(--color-border)",
          borderRadius: 8,
          padding: 2,
        }}
      >
        {OPTIONS.map(({ value, label, Icon }) => {
          const selected = preference === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={(e) => {
                e.stopPropagation();
                setPreference(value);
              }}
              title={label}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                padding: "6px 0",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                backgroundColor: selected ? "var(--color-bg-card)" : "transparent",
                color: selected ? "var(--color-text-strong)" : "var(--color-text-muted)",
                fontWeight: selected ? 700 : 500,
                fontSize: 10,
                boxShadow: selected ? "0 1px 2px rgba(0,0,0,0.12)" : "none",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <Icon size={14} strokeWidth={selected ? 2.4 : 1.8} aria-hidden="true" />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default AppearancePicker;
